<template>
  <v-container>
    <div class="elevation-2 px-5 mt-3">
      <SimplePageHeader title="Projects" class="px-4 pr-5 mb-5">
        <template v-slot:right-buttons>
          <ButtonCustomIcon
            icon="add"
            color="primary"
            text="Add"
            :block="true"
            @click="openProjectAddModalHandler"
          />
        </template>
      </SimplePageHeader>
      <v-row
        align="center"
        justify="start"
        class="px-5"
      >
        <v-col cols="12" sm="6" md="4" lg="3" xl="2"
          class="d-flex align-center py-5"
        >
          <v-checkbox
            v-model="internalFilter"
            label="Show internal projects only"
            hide-details
            class="mt-0 pt-0"
            @change="handleInternalFilterChange"
          ></v-checkbox>
        </v-col>
      </v-row>
      <v-row
        align="center"
        justify="start"
        class="px-5 mb-sm-5"
      >
        <v-col cols="12" sm="6" md="4" lg="3" xl="2">
          <ProjectsDropDownList
            v-model="projectNameFilter"
            label="Project Name"
            :clearable="true"
            :hideDetails="true"
          ></ProjectsDropDownList>
        </v-col>
        <v-col v-if="!internalFilter" cols="12" sm="6" md="4" lg="3" xl="2">
          <BusinessEntitiesDropDownList
            v-model="businessEntityIdFilter"
            label="Business Entity"
            :clearable="true"
            :hideDetails="true"
            @businessEntityIdChange="handleBusinessEntityIdChange"
          ></BusinessEntitiesDropDownList>
        </v-col>
        <v-col v-if="!internalFilter" cols="12" sm="6" md="4" lg="3" xl="2">
          <ClientsDropDownList
            :id="clientIdFilter"
            :businessEntityId="businessEntityIdFilter"
            :clearable="true"
            :hideDetails="true"
            @clientIdChange="handleClientIdChange"
          ></ClientsDropDownList>
        </v-col>
        <v-col cols="12" sm="6" md="4" lg="3" xl="2">
          <v-select
            v-model="statusFilter"
            :items="projectStatusList"
            item-value="id"
            item-text="name"
            label="Project Status"
            outlined
            clearable
            hide-details
          ></v-select>
        </v-col>
      </v-row>
      <v-row justify="end">
        <v-col cols="12" sm="6">
          <FilterButtons
            @search="handleSearchClick"
            @clear="handleClearClick"
          ></FilterButtons>
        </v-col>
      </v-row>
      <FilterButtonsSmall
        @search="handleSearchClick"
        @clear="handleClearClick"
      ></FilterButtonsSmall>

    </div>
    <v-container fluid class="px-0 pt-2 mt-4">
      <v-layout column>
        <v-data-table
          dense
          light
          :headers="headers"
          :items="projectsList"
          :options.sync="options"
          :server-items-length="serverItemsLength"
          item-key="id"
          class="elevation-2"
        >
          <template v-slot:item.internalProject="{ item }">
            <v-simple-checkbox
              v-model="item.internalProject"
              disabled
            ></v-simple-checkbox>
          </template>
          <template v-slot:item.anticipatedRevenue="{ item }">
            {{ item.anticipatedRevenue | currency }}
          </template>
          <template v-slot:item.action="{ item }">
            <v-btn class="mx-1 my-1"
                   color="primary"
                   outlined
                   @click="showEditProjectModal(item)"
            >
              <v-icon color="primary">edit</v-icon>
            </v-btn>
            <v-btn class="mx-1 my-1"
                   color="red"
                   outlined
                   @click="showDeleteProjectConfirmationDialog(item)"
            >
              <v-icon color="red">delete</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-layout>
    </v-container>
    <ConfirmationModal
      :showModal="showDeleteProjectConfirmationDialogProp"
      :title="'Delete Confirmation'"
      :contentText="'Are you sure you want to delete the selected project?'"
      :toolbarColor="'primary'"
      @cancel-click="closeDeleteProjectConfirmationDialogHandler"
      @ok-click="deleteProjectHandler"
    ></ConfirmationModal>
    <BottomSpacer></BottomSpacer>
  </v-container>
</template>

<script>
import { SimplePageHeader } from '@/components/pageComponents'
import { BottomSpacer, ButtonCustomIcon, FilterButtons, FilterButtonsSmall } from '@/components/buttons'
import { mapFields } from '@/vueHelpers/vuex'
import { mapActions } from 'vuex'
import { ConfirmationModal } from '@/components/dialogs'
import { ClientsDropDownList } from '@/components/clients'
import { BusinessEntitiesDropDownList } from '@/components/businessEntities'
import ProjectsDropDownList from '@/components/projects/ProjectsDropDownList'
import { projectCreateRoute, projectEditRoute } from '@/lib/constants/routes'
import { projectStatusList } from '@/lib/constants/projectStatuses'
import { parseGridOptionsFromQuery, buildPaginationQueryParams, isAllRecordsSortOnlyChange, setOrDeleteQueryParam, hasQueryChanged } from '@/lib/pagingAndSorting'

export default {
  components: {
    SimplePageHeader,
    BottomSpacer,
    ButtonCustomIcon,
    ConfirmationModal,
    ClientsDropDownList,
    BusinessEntitiesDropDownList,
    ProjectsDropDownList,
    FilterButtons,
    FilterButtonsSmall
  },
  data () {
    return {
      projectNameFilter: null,
      clientIdFilter: null,
      businessEntityIdFilter: null,
      headers: [
        { text: 'Project Name', value: 'projectName', width: '18%' },
        { text: 'Client Name', value: 'clientName', width: '16%' },
        { text: 'Status', value: 'status', width: '10%' },
        { text: 'Internal', value: 'internalProject', width: '10%' },
        { text: 'Anticipated Revenue', value: 'anticipatedRevenue', width: '16%' },
        { text: 'Actions', value: 'action', align: 'center', width: '20%', sortable: false }
      ],
      showDeleteProjectConfirmationDialogProp: false,
      projectIdToDelete: null,
      internalFilter: false,
      statusFilter: null,
      projectStatusList,
      hasFetchedAllProjects: false,
      options: {
        page: 1,
        itemsPerPage: 10,
        sortBy: ['projectName'],
        sortDesc: [false]
      },
      projectsList: []
    }
  },
  computed: {
    ...mapFields('projects', ['totalRecords']),
    serverItemsLength () {
      return this.options.itemsPerPage === -1 ? undefined : this.totalRecords
    }
  },
  watch: {
    options: {
      handler (newOptions, previousOptions) {
        this.syncOptionsToQuery()

        if (!this.shouldFetchProjects(newOptions, previousOptions)) {
          return
        }

        this.fetchProjects()
      },
      deep: true
    }
  },
  methods: {
    ...mapActions('projects', ['getProjects', 'getProjectsWithFiltersPaginated', 'deleteProject']),
    getOptionsFromQuery () {
      return parseGridOptionsFromQuery(this.$route.query, this.options)
    },
    applyFiltersFromQuery () {
      const { projectName, clientId, businessEntityId, statusId, internalProject } = this.$route.query

      this.projectNameFilter = typeof projectName === 'string' && projectName.length ? projectName : null

      const parsedClientId = Number(clientId)
      this.clientIdFilter = Number.isInteger(parsedClientId) && parsedClientId > 0 ? parsedClientId : null

      const parsedBusinessEntityId = Number(businessEntityId)
      this.businessEntityIdFilter = Number.isInteger(parsedBusinessEntityId) && parsedBusinessEntityId > 0 ? parsedBusinessEntityId : null

      const parsedStatusId = Number(statusId)
      this.statusFilter = Number.isInteger(parsedStatusId) && parsedStatusId > 0 ? parsedStatusId : null

      this.internalFilter = internalProject === 'true'
    },
    syncOptionsToQuery () {
      const { sortColumn, sortDirection, ...paginationParams } = buildPaginationQueryParams(this.options)

      const nextQuery = { ...this.$route.query, ...paginationParams }

      setOrDeleteQueryParam(nextQuery, 'sortColumn', sortColumn)
      setOrDeleteQueryParam(nextQuery, 'sortDirection', sortColumn ? sortDirection : null)

      setOrDeleteQueryParam(nextQuery, 'projectName', this.projectNameFilter)
      setOrDeleteQueryParam(nextQuery, 'clientId', this.clientIdFilter)
      setOrDeleteQueryParam(nextQuery, 'businessEntityId', this.businessEntityIdFilter)
      setOrDeleteQueryParam(nextQuery, 'statusId', this.statusFilter)
      setOrDeleteQueryParam(nextQuery, 'internalProject', this.internalFilter ? true : null)

      if (hasQueryChanged(this.$route.query, nextQuery, ['pageNumber', 'pageSize', 'sortColumn', 'sortDirection', 'projectName', 'clientId', 'businessEntityId', 'statusId', 'internalProject'])) {
        this.$router.replace({ query: nextQuery })
      }
    },
    getFiltersObject () {
      return {
        projectName: this.projectNameFilter,
        clientId: this.clientIdFilter,
        businessEntityId: this.businessEntityIdFilter,
        statusId: this.statusFilter,
        internalProject: this.internalFilter ? true : null
      }
    },
    fetchProjects () {
      const { page, itemsPerPage, sortBy, sortDesc } = this.options
      const filters = this.getFiltersObject()

      if (itemsPerPage === -1) {
        this.getProjects(filters).then(projects => {
          this.hasFetchedAllProjects = true
          this.projectsList = projects
        })
        return
      }

      this.hasFetchedAllProjects = false
      this.getProjectsWithFiltersPaginated({
        ...filters,
        pageNumber: page,
        pageSize: itemsPerPage,
        sortColumn: sortBy && sortBy.length ? sortBy[0] : null,
        sortDirection: sortDesc && sortDesc.length ? (sortDesc[0] ? 'DESC' : 'ASC') : null
      }).then(projects => {
        this.projectsList = projects
      })
    },
    shouldFetchProjects (newOptions, previousOptions) {
      return !isAllRecordsSortOnlyChange(newOptions, previousOptions, this.hasFetchedAllProjects)
    },
    handleClientIdChange (newValue) {
      this.clientIdFilter = newValue
    },
    handleBusinessEntityIdChange () {
      this.clientIdFilter = null
    },
    handleInternalFilterChange () {
      if (this.internalFilter) {
        this.clientIdFilter = null
        this.businessEntityIdFilter = null
      }
    },
    handleSearchClick () {
      this.options = { ...this.options, page: 1 }
    },
    handleClearClick () {
      this.projectNameFilter = null
      this.clientIdFilter = null
      this.businessEntityIdFilter = null
      this.statusFilter = null
      this.internalFilter = false
      this.options = { ...this.options, page: 1 }
    },
    openProjectAddModalHandler () {
      this.$router.push({ name: projectCreateRoute.name })
    },
    showEditProjectModal (item) {
      this.$router.push({ name: projectEditRoute.name, params: { id: item.id } })
    },
    showDeleteProjectConfirmationDialog (item) {
      this.projectIdToDelete = item.id
      this.showDeleteProjectConfirmationDialogProp = true
    },
    closeDeleteProjectConfirmationDialogHandler () {
      this.projectIdToDelete = null
      this.showDeleteProjectConfirmationDialogProp = false
    },
    async deleteProjectHandler () {
      await this.deleteProject(this.projectIdToDelete)
      this.showDeleteProjectConfirmationDialogProp = false
      this.fetchProjects()
    }
  },
  created () {
    this.applyFiltersFromQuery()
    this.options = {
      ...this.options,
      ...this.getOptionsFromQuery()
    }
    this.syncOptionsToQuery()
  }
}
</script>
